import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';

interface ICircleChart {
    percentage: number;
    secondaryPercentage?: number;
    strokeWidth?: number;
    label?: string
    fuel?: boolean
    secondaryPercentageColor?: {
        start: string;
        end: string;
    }
}

export const CircleChart: React.FC<ICircleChart> = ({
    percentage,
    secondaryPercentage,
    strokeWidth = 19,
    label,
    fuel,
    secondaryPercentageColor = { start: '#975b01', end: '#ff9900' }
}) => {
    const size = 150;
    const hasSecondary = typeof secondaryPercentage === 'number';


    const center = size / 2;
    const totalThickness = strokeWidth;
    const ringGap = 2; // px gap between inner and outer rings

    const effectiveThickness = hasSecondary ? Math.max(totalThickness - ringGap, 0) : totalThickness;
    const eachRingWidth = hasSecondary ? effectiveThickness / 2 : totalThickness;

    // Keep outermost edge at the same place so chart fits exactly as before.
    const outerRadius = center - eachRingWidth / 2;
    const innerRadius = hasSecondary
        ? outerRadius - eachRingWidth - ringGap
        : outerRadius;

    const innerCircumference = 2 * Math.PI * innerRadius;
    const clampedPrimary = Math.max(0, Math.min(percentage, 100));
    const clampedSecondary = Math.max(0, Math.min(secondaryPercentage ?? 0, 100));

    const innerValue = hasSecondary ? clampedSecondary : clampedPrimary;
    const innerOffset = innerCircumference - (innerValue / 100) * innerCircumference;

    const outerCircumference = 2 * Math.PI * outerRadius;
    const outerOffset = outerCircumference - (clampedPrimary / 100) * outerCircumference;

    const { darkMode } = useSelector((state: RootState) => state.globals);


    const colors = {
        red: {
            start: '#861409',
            end: '#CF1300'
        },
        green: {
            start: '#0A4D00',
            end: '#1B6F00'
        },
        yellow: {
            start: '#5A4B00',
            end: '#8C6F00'
        }
    }

    const startColor = fuel
        ? (percentage <= 10 ? colors.red.start : percentage <= 20 ? colors.yellow.start : colors.green.start)
        : (percentage >= 90 ? colors.red.start : percentage >= 80 ? colors.yellow.start : colors.green.start);

    const endColor = fuel
        ? (percentage <= 10 ? colors.red.end : percentage <= 20 ? colors.yellow.end : colors.green.end)
        : (percentage >= 90 ? colors.red.end : percentage >= 80 ? colors.yellow.end : colors.green.end);

    return (
        <svg width={size} height={size}>
            <defs>
                <linearGradient id='gradient' x1='0%' y1='0%' x2='100%' y2='0%'>
                    <stop offset='32%' stopColor={startColor} />
                    <stop offset='81%' stopColor={endColor} />
                </linearGradient>
                {hasSecondary && (
                    <linearGradient id='secondaryGradient' x1='0%' y1='0%' x2='100%' y2='0%'>
                        <stop offset='32%' stopColor={secondaryPercentageColor.start} />
                        <stop offset='81%' stopColor={secondaryPercentageColor.end} />
                    </linearGradient>
                )}
            </defs>

            {/* Background track for inner/original ring */}
            <circle
                cx={size / 2}
                cy={size / 2}
                r={innerRadius}
                stroke='#4f4434'
                strokeWidth={eachRingWidth}
                fill='none'
                opacity='0.2'
            />

            {hasSecondary && (
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={outerRadius}
                    stroke='#4f4434'
                    strokeWidth={eachRingWidth}
                    fill='none'
                    opacity='0.2'
                />
            )}

            <circle
                cx={size / 2}
                cy={size / 2}
                r={innerRadius}
                stroke={hasSecondary ? 'url(#secondaryGradient)' : 'url(#gradient)'}
                strokeWidth={eachRingWidth}
                fill='none'
                strokeDasharray={innerCircumference}
                strokeDashoffset={innerOffset}
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />

            {/* Primary progress arc (outer ring when secondary exists) */}
            {hasSecondary && (
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={outerRadius}
                    stroke='url(#gradient)'
                    strokeWidth={eachRingWidth}
                    fill='none'
                    strokeDasharray={outerCircumference}
                    strokeDashoffset={outerOffset}
                    transform={`rotate(-90 ${size / 2} ${size / 2})`}
                />
            )}

            {/* Center text */}
            <text
                x='50%'
                y='50%'
                dominantBaseline='middle'
                textAnchor='middle'
                fontSize='24'
                fontWeight='bold'
                fill={darkMode ? '#fff' : '#46425'}
            >
                {label ?? percentage}%
            </text>
        </svg>
    );
};
